import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

const DARK_BG = {
  background: "radial-gradient(ellipse at top, #3D0810 0%, #1a0205 40%, #0f0102 100%)",
};

export default function StudioLoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const handleLogin = async () => {
    if (!username.trim()) { toast.error("أدخل اسم المستخدم"); return; }
    if (!password.trim()) { toast.error("أدخل كلمة المرور"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/studio/auth/login`, {
        username: username.trim(),
        password,
      });
      localStorage.setItem("studio_token", data.token);
      localStorage.setItem("studio_user", JSON.stringify({
        name: data.name,
        role: data.role,
        studio_role: data.studio_role,
      }));
      toast.success(`أهلاً ${data.name}! مرحباً في الاستوديو`);
      navigate("/studio/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "بيانات الدخول غير صحيحة");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={DARK_BG}
      dir="rtl"
    >
      {/* Subtle star/sparkle pattern overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23F1E194' fill-opacity='0.04'%3E%3Cpath d='M40 0L50 20L70 20L55 32L60 52L40 40L20 52L25 32L10 20L30 20Z'/%3E%3C/g%3E%3C/svg%3E\")",
        }}
      />

      <div className="w-full max-w-sm relative z-10">
        <button
          onClick={() => navigate("/")}
          className="text-secondary/50 hover:text-secondary mb-6 flex items-center gap-2 transition-colors"
        >
          ← رجوع للرئيسية
        </button>

        <div
          className="rounded-3xl p-8 text-center"
          style={{
            background: "rgba(91, 14, 20, 0.55)",
            border: "1px solid rgba(241, 225, 148, 0.25)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 0 60px rgba(91, 14, 20, 0.4), 0 0 0 1px rgba(241, 225, 148, 0.08)",
          }}
        >
          {/* Logo */}
          <div className="mb-2 flex items-center justify-center gap-2">
            <span style={{ fontSize: 38 }}>✦</span>
          </div>
          <h1 className="text-3xl font-black mb-1" style={{ color: "#F1E194" }}>
            HUJJAH Studio
          </h1>
          <p className="text-sm mb-8" style={{ color: "rgba(241,225,148,0.5)" }}>
            بوابة فريق المحتوى
          </p>

          {/* Username */}
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="اسم المستخدم"
            className="w-full px-4 py-3 rounded-xl text-sm font-bold outline-none transition-all text-center mb-3"
            style={{
              background: "rgba(26, 2, 5, 0.6)",
              border: "2px solid rgba(241,225,148,0.25)",
              color: "#F1E194",
            }}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            onFocus={(e) => (e.target.style.borderColor = "rgba(241,225,148,0.7)")}
            onBlur={(e) => (e.target.style.borderColor = "rgba(241,225,148,0.25)")}
          />

          {/* Password */}
          <div className="relative mb-6">
            <input
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="كلمة المرور"
              className="w-full px-4 py-3 pl-12 rounded-xl text-lg font-bold outline-none transition-all text-center"
              style={{
                background: "rgba(26, 2, 5, 0.6)",
                border: "2px solid rgba(241,225,148,0.25)",
                color: "#F1E194",
              }}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              onFocus={(e) => (e.target.style.borderColor = "rgba(241,225,148,0.7)")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(241,225,148,0.25)")}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              className="absolute left-3 top-1/2 -translate-y-1/2 transition-colors"
              style={{ color: "rgba(241,225,148,0.4)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(241,225,148,0.8)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(241,225,148,0.4)")}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full font-black text-lg py-3 rounded-full transition-all duration-300 disabled:opacity-50"
            style={{
              background: loading ? "rgba(241,225,148,0.6)" : "#F1E194",
              color: "#1a0205",
              transform: loading ? "scale(1)" : undefined,
            }}
            onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = "scale(1.03)")}
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            {loading ? "جاري الدخول..." : "دخول الاستوديو ✦"}
          </button>
        </div>

        <p className="text-center mt-4 text-xs" style={{ color: "rgba(241,225,148,0.25)" }}>
          حُجّة © {new Date().getFullYear()} — للفريق الداخلي فقط
        </p>
      </div>
    </div>
  );
}
